// Wave 20.14 — /discover
//
// Three tabs sharing one users-list component:
//   Discover  — search + suggested people to follow
//   Following — users the current user follows
//   Followers — users following the current user
//
// Each row has an inline FollowButton for one-tap follow / unfollow.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import FollowButton from './components/FollowButton';
import AeroHeatmap from './components/AeroHeatmap';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

type TabKey = 'discover' | 'following' | 'followers' | 'heatmap';

interface Row {
  id: number;
  username: string;
  displayName: string;
}

async function fetchJson(path: string): Promise<any> {
  const jwt = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}${path}`, {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}

export default function Discover() {
  const [tab, setTab] = useState<TabKey>('discover');

  // Discover tab state
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Row[] | null>(null);
  const [suggested, setSuggested] = useState<Row[] | null>(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<any>(null);

  // Following / Followers state — fetched on demand, cached after
  // first load so switching tabs doesn't re-hit the endpoint.
  const [following, setFollowing] = useState<Row[] | null>(null);
  const [followers, setFollowers] = useState<Row[] | null>(null);

  useEffect(() => {
    fetchJson('/users/suggested')
      .then((r) => setSuggested(Array.isArray(r) ? r : []))
      .catch(() => setSuggested([]));
  }, []);

  useEffect(() => {
    if (tab === 'following' && following === null) {
      fetchJson('/follow/me/following')
        .then((r) => setFollowing(Array.isArray(r) ? r : []))
        .catch(() => setFollowing([]));
    }
    if (tab === 'followers' && followers === null) {
      fetchJson('/follow/me/followers')
        .then((r) => setFollowers(Array.isArray(r) ? r : []))
        .catch(() => setFollowers([]));
    }
  }, [tab, following, followers]);

  const runSearch = useCallback((term: string) => {
    if (term.trim().length < 2) {
      setResults(null);
      return;
    }
    setSearching(true);
    fetchJson(`/users/search?q=${encodeURIComponent(term.trim())}`)
      .then((r) => setResults(Array.isArray(r) ? r : []))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 250);
    return () => clearTimeout(debounceRef.current);
  }, [q, runSearch]);

  return (
    <PageLayout maxWidth="md">
      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
            Discover
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Find others to follow
          </Typography>
          <Typography sx={{ color: colors.textMuted, mt: 0.5 }}>
            Search below, or browse who follows you and who you follow.
          </Typography>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as TabKey)}
          sx={{
            borderBottom: `1px solid ${colors.border}`,
            '& .MuiTab-root': {
              color: colors.textMuted,
              fontWeight: 700,
              textTransform: 'none',
            },
            '& .Mui-selected': { color: `${colors.brand} !important` },
            '& .MuiTabs-indicator': { backgroundColor: colors.brand },
          }}
        >
          <Tab value="discover" label="Discover" />
          <Tab
            value="following"
            label={`Following${following ? ` · ${following.length}` : ''}`}
          />
          <Tab
            value="followers"
            label={`Followers${followers ? ` · ${followers.length}` : ''}`}
          />
          <Tab value="heatmap" label="Heat map" />
        </Tabs>

        {tab === 'discover' && (
          <>
            <TextField
              fullWidth
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by username or name…"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: colors.textMuted, mr: 1 }} />,
                endAdornment: searching ? <CircularProgress size={18} /> : null,
              }}
            />
            {results !== null ? (
              <Section title="Search results" empty="No matches yet.">
                {results.map((u) => (
                  <UserRow key={u.id} u={u} />
                ))}
              </Section>
            ) : suggested === null ? (
              <Loader />
            ) : (
              <Section title="Suggested" empty="No suggestions right now — try a search above.">
                {suggested.map((u) => (
                  <UserRow key={u.id} u={u} />
                ))}
              </Section>
            )}
          </>
        )}

        {tab === 'following' && (
          following === null ? (
            <Loader />
          ) : (
            <Section
              title="You follow"
              empty="You're not following anyone yet — head to Discover."
            >
              {following.map((u) => (
                <UserRow key={u.id} u={u} />
              ))}
            </Section>
          )
        )}

        {tab === 'followers' && (
          followers === null ? (
            <Loader />
          ) : (
            <Section
              title="Following you"
              empty="No one's following you yet — share a Drop Link to get noticed."
            >
              {followers.map((u) => (
                <UserRow key={u.id} u={u} />
              ))}
            </Section>
          )
        )}

        {tab === 'heatmap' && <AeroHeatmap />}
      </Stack>
    </PageLayout>
  );
}

function Loader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress sx={{ color: colors.brand }} />
    </Box>
  );
}

function Section({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode;
}) {
  const childArray = React.Children.toArray(children);
  return (
    <Box>
      <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.15em' }}>
        {title}
      </Typography>
      {childArray.length === 0 ? (
        <Typography sx={{ color: colors.textMuted, fontStyle: 'italic', mt: 1 }}>{empty}</Typography>
      ) : (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {childArray}
        </Stack>
      )}
    </Box>
  );
}

function UserRow({ u }: { u: Row }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <RouterLink to={`/u/${u.username}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography sx={{ fontWeight: 800, color: colors.text }}>
            {u.displayName || u.username}
          </Typography>
        </RouterLink>
        {u.username && (
          <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
            @{u.username}
          </Typography>
        )}
      </Box>
      <FollowButton userId={u.id} compact />
    </Box>
  );
}
